import { isEmpty } from '@ember/utils';
import { set, computed } from '@ember/object';
import Enum from '@square/dashboard-utilities/utils/enum';
import { isProfileDirtyProperty, inflateProfile } from '@square/dashboard-data/models/directory/contact-profile';
import ContactOptions from '@square/dashboard-data/models/squareup/rolodex/contact-options';
import { ContactBatchActionStatus } from '@square/dashboard-data/models/squareup/rolodex/batch-contacts-group';
import { ContactSet } from '@square/dashboard-data/models/squareup/rolodex/contact-set';
import Error from '@square/dashboard-data/models/squareup/rolodex/error';
import GlobalAddress from '@square/dashboard-data/models/squareup/common/location/global-address';
import ApplicationFields from '@square/dashboard-data/models/squareup/rolodex/application-fields';
import Attachment from '@square/dashboard-data/models/squareup/rolodex/attachment';
import { Attribute } from '@square/dashboard-data/models/squareup/rolodex/attributes';
import AppField from '@square/dashboard-data/models/squareup/rolodex/app-field';
import BuyerSummary from '@square/dashboard-data/models/squareup/rolodex/buyer-summary';
import Group from '@square/dashboard-data/models/squareup/rolodex/group';
import QueryContext from '@square/dashboard-data/models/squareup/rolodex/query-context';
import { RequestType as GdprRequestType } from '@square/dashboard-data/models/squareup/rolodex/gdpr-log';
import { RequestType, FlowType } from '@square/dashboard-data/models/squareup/rolodex/privacy-log';
import InstrumentsOnFile from '@square/dashboard-data/models/squareup/rolodex/instruments-on-file';
import { Note } from '@square/dashboard-data/models/squareup/rolodex/note';
import MoneyProto from '@square/dashboard-data/models/squareup/common/money-proto';
import { EventOption, ListOption } from '@square/dashboard-data/models/squareup/rolodex/cedas';
import ContactEvent from '@square/dashboard-data/models/squareup/rolodex/contact-event';
import { YearMonthDay } from '@square/dashboard-data/models/squareup/common/time';
import { ProtoBaseModel, attr, repeated } from '@square/dashboard-data/utils/ember-model-stubs';
import { Filters, Sort, PaginationRequest, PaginationResponse } from '@square/dashboard-data/models/squareup/rolodexdiscovery/filter';
import { Code } from '@square/dashboard-data/models/squareup/rolodex/api';
export const DEFAULT_PROFILE_CONTACT_OPTIONS = {
  includeApplicationFields: true,
  includeAttributes: true,
  includeGroups: true
};

// TODO this should be fixed after we migrated to new instrument endpoints
// Temporary fix to work around https://jira.sqcorp.co/browse/BILL-578
function fixInstrumentsOneOf(contact) {
  const instruments = ((contact || {}).instruments_on_file?.instrument || []).filter(instrument => instrument !== null && instrument !== undefined);
  for (const instrument of instruments) {
    instrument.card = instrument.InstrumentData?.Card || instrument.card;
    delete instrument.InstrumentData;
  }
}
const EmailSubscriptions = ProtoBaseModel.extend({
  canReceiveDirectMessaging: attr(Boolean)
});
export const ContactSource = new Enum(['APPOINTMENTS', 'COUPON_ISSUE_FEED', 'COUPON_REDEMPTION_FEED', 'EMAIL_COLLECTION', 'EXTERNAL_PLATFORM', 'FEEDBACK', 'GIFTCARD', 'IMPORT', 'INSTRUMENTS_FEED', 'INVOICE', 'LOYALTY', 'MANUAL', 'MARKETING', 'MARKETING_FEED', 'MERGE', 'NOTE', 'ONLINE_STORE', 'PAYMENTS_FEED', 'POINT_OF_SALE', 'RECOVERED_FROM_DELETED', 'RETAIL_POS', 'TERMINAL', 'THIRD_PARTY_IMPORT']);
export const PaymentCard = ProtoBaseModel.extend({
  panFideliusToken: attr(String),
  brandName: attr(String),
  lastFour: attr(String),
  transactionCount: attr(Number, {
    defaultValue: 0
  })
});
export const Payment = ProtoBaseModel.extend({
  amount: attr(MoneyProto),
  contactToken: attr(String),
  merchantToken: attr(String),
  nameOnCard: attr(String),
  panFideliusToken: attr(String),
  paymentToken: attr(String)
});
export const Profile = ProtoBaseModel.extend({
  address: attr(GlobalAddress),
  birthday: attr(YearMonthDay),
  cashTag: attr(String),
  companyName: attr(String),
  emailAddress: attr(String),
  givenName: attr(String),
  memo: attr(String),
  nickname: attr(String),
  phoneNumber: attr(String),
  phoneticGivenName: attr(String),
  phoneticSurname: attr(String),
  referenceId: attr(String),
  surname: attr(String),
  taxId: attr(String),
  timeZone: attr(String),
  version: attr(String),
  // TODO(lenny) remove all empty fields?
  toJSON(...args) {
    const data = this._super(...args);

    // Rolodex expects missing keys, not null or 0 values. This hack must
    // go here because we don't want to customize the common YearMonthDay class.
    const birthday = data.birthday;
    if (birthday) {
      for (const key in birthday) {
        if (isEmpty(birthday[key])) {
          delete birthday[key];
        }
      }
      set(data, 'birthday', birthday);
    }
    return data;
  }
});
export const ObfuscatedIdentifiers = ProtoBaseModel.extend({
  obfuscatedEmailAddress: attr(String),
  obfuscatedPhoneNumber: attr(String),
  obfuscatedAddress: attr(String)
});
const Contact = ProtoBaseModel.extend({
  contactToken: attr(String),
  merchantToken: attr(String),
  authorToken: attr(String),
  createdAtMs: attr(Number),
  updatedAtMs: attr(Number),
  displayName: attr(String),
  receiptEmailAddressObfuscated: attr(String),
  group: repeated(Group),
  automatic: attr(Boolean),
  emailable: attr(Boolean),
  source: attr(ContactSource),
  sourceLabel: attr(String),
  buyerSummary: attr(BuyerSummary),
  instrumentsOnFile: attr(InstrumentsOnFile),
  linkedPaymentCards: repeated(PaymentCard),
  profile: attr(Profile),
  phoneId: attr(Number),
  emailId: attr(Number),
  emailSubscriptions: attr(EmailSubscriptions),
  schemaVersion: attr(String),
  attributes: repeated(Attribute),
  attachments: repeated(Attachment),
  totalDuplicateCount: attr(Number),
  notes: repeated(Note),
  appFields: repeated(AppField),
  applicationFields: attr(ApplicationFields),
  obfuscatedIdentifiers: attr(ObfuscatedIdentifiers),
  houseAccounts: repeated(),
  // This should be passed a ProtoBaseModel class

  // instruments
  linkingHistory: null,
  isProfileDirty: isProfileDirtyProperty(),
  isDirty: computed('_dirtyAttributes.[]', 'isProfileDirty', 'group.@each.isDirty', function (...args) {
    // Model attrs must be checked for dirtiness manually.
    // Whether items are added or removed to group array is not necessary since all interactions
    // with group replace the group array entirely.
    return this._super(...args) || this.isProfileDirty || (this.group || []).isAny('isDirty');
  }),
  addCardOnFile(newCardSummary) {
    if (!this.instrumentsOnFile) {
      this.set('instrumentsOnFile', InstrumentsOnFile.create());
    }
    return this.instrumentsOnFile?.instrument.pushObject(newCardSummary);
  },
  inflate() {
    // This is required to setup sub-models since they may not be automatically
    // created if they are not part of the original json hash during create, since
    // attr with DeprecatedEmberModelBaseModel get does not return an object if it's never set.
    if (!this.profile) {
      this.set('profile', Profile.create());
    }
    inflateProfile(this.profile);
  },
  toJSON() {
    const data = this._super();
    // wipe properties fo null, undefined, and empty string values
    for (const key in data) {
      if (isEmpty(data[key])) {
        delete data[key];
      }
    }
    return data;
  }
});

// request / response
export const BatchGetContactsRequest = ProtoBaseModel.extend({
  merchantToken: repeated(String),
  contactToken: repeated(String),
  asMap: attr(Boolean),
  contactOptions: attr(ContactOptions)
});

// NOTE: this always assumes the BatchGetContactsRequest to be sent with
// `asMap: true` and should not be used otherwise.
export const BatchGetContactsResponse = ProtoBaseModel.extend({
  contact: repeated(Contact),
  contactsMap: attr(Object, {
    defaultValue: {}
  }),
  error: attr(Error)
});
BatchGetContactsResponse.reopenClass({
  deserialize(data) {
    const contactsMap = (data || {}).contacts_map;
    for (const [key, value] of Object.entries(contactsMap)) {
      const contact = Contact.deserialize(value);
      fixInstrumentsOneOf(contact);
      set(contactsMap, `${key}`, contact);
    }
    const contacts = ((data || {}).contact || []).filter(contact => contact !== null && contact !== undefined);
    for (const contact of contacts) {
      fixInstrumentsOneOf(contact);
    }
    return this._super(data);
  }
});
export const BatchGetContactsResponseV2 = ProtoBaseModel.extend({
  contact: repeated(Contact),
  error: attr(Error)
});
export const CreateContactRequest = ProtoBaseModel.extend({
  merchantToken: attr(String),
  requestToken: attr(String),
  contact: attr(Contact)
});
export const CreateContactResponse = ProtoBaseModel.extend({
  contact: attr(Contact),
  code: attr(Code),
  errorDetail: attr(Error)
});
export const DeleteContactRequest = ProtoBaseModel.extend({
  merchantToken: repeated(String),
  contact: attr(Contact),
  gdprRequestType: attr(GdprRequestType)
});
export const DeleteContactResponse = ProtoBaseModel.extend({
  success: attr(Boolean)
});

// Definition: https://prototype.sqprod.co/#/docs/squareup.rolodex.DeleteContactsResponse.Error
export const DeleteContactsResponseErrorCode = new Enum(['BULK_DELETE_SIZE_LIMIT_EXCEEDED']);
export const DeleteContactsResponseError = ProtoBaseModel.extend({
  code: attr(DeleteContactsResponseErrorCode)
});
export const DeleteContactsRequest = ProtoBaseModel.extend({
  merchantToken: attr(String),
  contactSet: attr(ContactSet)
});
export const DeleteContactsResponse = ProtoBaseModel.extend({
  success: attr(Boolean),
  batchActionStatus: attr(ContactBatchActionStatus),
  error: attr(DeleteContactsResponseError)
});
export const GetContactsForPaymentsRequest = ProtoBaseModel.extend({
  merchantToken: attr(String),
  paymentToken: attr(String),
  contactOptions: attr(ContactOptions)
});
export const ContactPaymentPair = ProtoBaseModel.extend({
  paymentToken: attr(String),
  contact: attr(Contact)
});
export const GetContactsForPaymentsResponse = ProtoBaseModel.extend({
  contactPaymentPairs: repeated(ContactPaymentPair)
});
export const GetContactRequest = ProtoBaseModel.extend({
  merchantToken: repeated(String),
  contactToken: attr(String),
  contactOptions: attr(ContactOptions)
});
export const GetContactResponse = ProtoBaseModel.extend({
  contact: attr(Contact)
});
GetContactResponse.reopenClass({
  deserialize(data) {
    fixInstrumentsOneOf((data || {}).contact);
    return this._super(data);
  }
});
export const ListContactsRequest = ProtoBaseModel.extend({
  merchantToken: repeated(String),
  context: attr(QueryContext),
  contactOptions: attr(ContactOptions)
});
export const ListContactsResponse = ProtoBaseModel.extend({
  contact: repeated(Contact),
  pagingKey: attr(String),
  total: attr(Number),
  searchId: attr(String)
});
ListContactsResponse.reopenClass({
  deserialize(data) {
    const contacts = ((data || {}).contact || []).filter(contact => contact !== null && contact !== undefined);
    for (const contact of contacts) {
      fixInstrumentsOneOf(contact);
    }
    return this._super(data);
  }
});
export const ListEventsForContactRequest = ProtoBaseModel.extend({
  eventOption: attr(EventOption),
  listOption: attr(ListOption),
  contactToken: attr(String),
  merchantToken: attr(String)
});
export const ListEventsForContactResponse = ProtoBaseModel.extend({
  event: repeated(ContactEvent),
  listOption: attr(ListOption)
});
export const UpdateContactProfileRequest = ProtoBaseModel.extend({
  applicationFields: attr(ApplicationFields),
  merchantToken: attr(String),
  contactToken: attr(String),
  contactOptions: attr(ContactOptions),
  profile: attr(Profile),
  group: repeated(Group),
  schemaVersion: attr(String),
  attributes: repeated(Attribute),
  gdprRequestType: attr(RequestType),
  flowType: attr(FlowType)
});
export const UpdateContactProfileResponse = ProtoBaseModel.extend({
  applicationFields: attr(ApplicationFields),
  profile: attr(Profile),
  group: repeated(Group),
  attributes: repeated(Attribute),
  schemaVersion: attr(String),
  displayName: attr(String),
  code: attr(Code),
  errorDetail: attr(Error)
});
export const SearchContactsRequest = ProtoBaseModel.extend({
  merchantToken: attr(String),
  query: attr(String),
  filters: repeated(Filters),
  pagination: attr(PaginationRequest),
  sort: attr(Sort),
  contactOptions: attr(ContactOptions)
});
export const SearchContactsResponse = ProtoBaseModel.extend({
  contacts: repeated(Contact),
  pagination: attr(PaginationResponse),
  searchId: attr(String)
});
export default Contact;